/**
 * useToast Hook
 * Feature: 034-global-error-handling
 * Constitution: v1.11.0 - Principle XI (Web Application Architecture)
 * 
 * @description Custom hook for displaying toast notifications
 * @implements FR-005, FR-006, FR-007, FR-008
 * @author Peter Dobler
 * @created 2026-01-20
 */

import { useCallback, useEffect } from 'react';
import toast, { ToastOptions, Renderable } from 'react-hot-toast';

/**
 * Toast types supported by the hook
 */
export type ToastType = 'success' | 'info' | 'warning' | 'error';

/**
 * Options for toast notifications
 */
export interface UseToastOptions extends Partial<ToastOptions> {
  /** Toast message */
  message: string;
  /** Optional action button */
  action?: {
    label: string;
    onClick: () => void;
  };
}

/**
 * Promise toast options
 */
export interface PromiseToastOptions<T> {
  /** Promise to track */
  promise: Promise<T>;
  /** Loading message */
  loading: string;
  /** Success message or function to generate message from result */
  success: string | ((data: T) => string);
  /** Error message or function to generate message from error */
  error: string | ((err: Error) => string);
}

/**
 * Return type for useToast hook
 */
export interface UseToastReturn {
  /** Show success toast */
  success: (message: string, options?: Partial<ToastOptions>) => string;
  /** Show info toast */
  info: (message: string, options?: Partial<ToastOptions>) => string;
  /** Show warning toast */
  warning: (message: string, options?: Partial<ToastOptions>) => string;
  /** Show error toast */
  error: (message: string, options?: Partial<ToastOptions>) => string;
  /** Show promise toast (loading → success/error) */
  promise: <T>(options: PromiseToastOptions<T>) => Promise<T>;
  /** Dismiss a specific toast by ID */
  dismiss: (toastId?: string) => void;
  /** Dismiss all toasts */
  dismissAll: () => void;
}

/**
 * Custom styling for different toast types
 */
const toastStyles: Record<ToastType, Partial<ToastOptions>> = {
  success: {
    duration: 3000,
    style: {
      borderLeft: '4px solid #10b981',
    },
    iconTheme: {
      primary: '#10b981',
      secondary: '#fff',
    },
  },
  info: {
    duration: 5000,
    icon: 'ℹ️',
    style: {
      borderLeft: '4px solid #3b82f6',
    },
  },
  warning: {
    duration: 7000,
    icon: '⚠️',
    style: {
      borderLeft: '4px solid #f59e0b',
    },
  },
  error: {
    duration: Infinity, // Error toasts persist until dismissed
    style: {
      borderLeft: '4px solid #ef4444',
    },
    iconTheme: {
      primary: '#ef4444',
      secondary: '#fff',
    },
  },
};

/**
 * useToast Hook
 * 
 * @example
 * ```tsx
 * const { success, error, promise } = useToast();
 * 
 * // Simple toasts
 * success('Operation completed');
 * error('Something went wrong');
 * 
 * // Promise toast
 * await promise({
 *   promise: saveData(),
 *   loading: 'Saving...',
 *   success: 'Saved!',
 *   error: 'Failed to save',
 * });
 * ```
 */
export const useToast = (): UseToastReturn => {
  /**
   * Set up keyboard dismissal support (Escape key)
   * FR-008: Keyboard accessibility
   */
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        toast.dismiss();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  /**
   * Show success toast
   * FR-005: Success notifications (green styling)
   */
  const success = useCallback((message: string, options?: Partial<ToastOptions>): string => {
    return toast.success(message, {
      ...toastStyles.success,
      ...options,
    });
  }, []);

  /**
   * Show info toast
   * FR-005: Informational notifications (blue styling)
   */
  const info = useCallback((message: string, options?: Partial<ToastOptions>): string => {
    return toast(message, {
      ...toastStyles.info,
      ...options,
    });
  }, []);

  /**
   * Show warning toast
   * FR-005: Warning notifications (yellow styling)
   */
  const warning = useCallback((message: string, options?: Partial<ToastOptions>): string => {
    return toast(message, {
      ...toastStyles.warning,
      ...options,
    });
  }, []);

  /**
   * Show error toast
   * FR-005: Error notifications (red styling, persistent)
   */
  const error = useCallback((message: string, options?: Partial<ToastOptions>): string => {
    return toast.error(message, {
      ...toastStyles.error,
      ...options,
    });
  }, []);

  /**
   * Show promise toast for async operations
   * FR-007: Loading states for async operations
   */
  const promiseToast = useCallback(<T,>(options: PromiseToastOptions<T>): Promise<T> => {
    return toast.promise(
      options.promise,
      {
        loading: options.loading,
        success: options.success as Renderable,
        error: options.error as Renderable,
      },
      {
        loading: {
          style: {
            borderLeft: '4px solid #6366f1',
          },
        },
        success: {
          duration: 3000,
          style: {
            borderLeft: '4px solid #10b981',
          },
        },
        error: {
          duration: Infinity,
          style: {
            borderLeft: '4px solid #ef4444',
          },
        },
      }
    );
  }, []);

  /**
   * Dismiss a specific toast by ID
   */
  const dismiss = useCallback((toastId?: string): void => {
    if (toastId) {
      toast.dismiss(toastId);
    } else {
      toast.dismiss();
    }
  }, []);

  /**
   * Dismiss all toasts
   */
  const dismissAll = useCallback((): void => {
    toast.dismiss();
  }, []);

  return {
    success,
    info,
    warning,
    error,
    promise: promiseToast,
    dismiss,
    dismissAll,
  };
};

export default useToast;
